import React, { useState, useEffect, useRef } from 'react';

const About = () => {
  const [currentStat, setCurrentStat] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [activeMilestone, setActiveMilestone] = useState(2);
  const sectionRef = useRef(null);

  // Define statistics array outside of useEffect to avoid dependency issues
  const statistics = [
    { 
      value: "45%", 
      label: "Higher Efficiency", 
      description: "Than fixed solar systems",
      icon: "⚡",
      color: "from-yellow-500 to-orange-500"
    },
    { 
      value: "500+", 
      label: "Installations", 
      description: "Across Egypt and MENA",
      icon: "🏗️",
      color: "from-blue-500 to-cyan-600"
    },
    { 
      value: "25+", 
      label: "Years Warranty", 
      description: "On all our systems",
      icon: "🛡️",
      color: "from-green-500 to-emerald-600"
    },
    { 
      value: "98%", 
      label: "Uptime", 
      description: "Guaranteed reliability",
      icon: "📈",
      color: "from-purple-500 to-pink-600"
    }
  ];

  useEffect(() => {
    // Store the current ref value in a variable for cleanup
    const currentSectionRef = sectionRef.current;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentSectionRef) {
      observer.observe(currentSectionRef);
    }

    const statInterval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % statistics.length);
    }, 4000);
    
    return () => {
      if (currentSectionRef) {
        observer.unobserve(currentSectionRef);
      }
      clearInterval(statInterval);
    };
  }, [statistics.length]); // Added statistics.length as dependency

  // Function to scroll to contact section
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      const offsetTop = contactSection.offsetTop - 80; // Account for fixed header
      window.scrollTo({ 
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  // Function to scroll to features section for technology exploration
  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      const offsetTop = featuresSection.offsetTop - 80; // Account for fixed header
      window.scrollTo({ 
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const teamMembers = [
    {
      name: "Dr. Ahmed Hassan",
      role: "Chief Technology Officer",
      expertise: "PhD in Renewable Energy, 15+ years in solar technology",
      achievements: ["25+ Patents", "MIT Alumni", "UN Energy Advisor"]
    },
    {
      name: "Eng. Mohamed Ali",
      role: "Head of Engineering",
      expertise: "Mechanical Engineering Specialist, Solar Tracking Systems",
      achievements: ["50+ Projects", "German Engineering Cert", "Industry Speaker"]
    },
    {
      name: "Dr. Fatima Mahmoud",
      role: "Research Director",
      expertise: "Materials Science, Advanced Solar Cell Technology",
      achievements: ["PhD Cambridge", "Research Grants", "Published Author"]
    }
  ];

  const milestones = [
    { 
      year: "2018", 
      event: "Company Founded", 
      description: "Started with a vision to revolutionize solar tracking",
      icon: "🚀",
      achievements: ["Initial R&D", "Team Assembly", "Prototype Design"]
    },
    { 
      year: "2019", 
      event: "First Prototype", 
      description: "Developed our first dual-axis tracking system",
      icon: "🔧",
      achievements: ["Testing Phase", "Performance Validation", "Client Demos"]
    },
    { 
      year: "2020", 
      event: "Commercial Launch", 
      description: "First commercial installation completed",
      icon: "🏢",
      achievements: ["First Client", "Revenue Generation", "Market Entry"]
    },
    { 
      year: "2022", 
      event: "MENA Expansion", 
      description: "Expanded operations across Middle East and North Africa",
      icon: "🌍",
      achievements: ["Regional Offices", "Local Partnerships", "Market Leadership"]
    },
    { 
      year: "2023", 
      event: "AI Integration", 
      description: "Launched AI-powered tracking algorithms",
      icon: "🤖",
      achievements: ["Machine Learning", "Predictive Analytics", "Smart Optimization"]
    }
  ];

  const values = [
    {
      title: "Innovation",
      description: "Pushing boundaries in solar technology",
      icon: "💡",
      color: "from-blue-500 to-cyan-500"
    },
    {
      title: "Sustainability",
      description: "Committed to environmental stewardship",
      icon: "🌱",
      color: "from-green-500 to-emerald-500"
    },
    {
      title: "Excellence",
      description: "German precision meets Egyptian craftsmanship",
      icon: "⭐",
      color: "from-yellow-500 to-orange-500"
    },
    {
      title: "Community",
      description: "Empowering local communities through solar",
      icon: "🤝",
      color: "from-purple-500 to-pink-500"
    }
  ];

  // Safe image URL with fallback
  const solarImageUrl = "https://images.unsplash.com/photo-1626618012641-bfbca5a31239?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80";

  return (
    <section ref={sectionRef} id="about" className="relative py-16 lg:py-24 bg-gradient-to-br from-white via-gray-50 to-primary-50/20 overflow-hidden">
      {/* Simplified Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-primary-200/20 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-200/20 rounded-full blur-3xl -z-10"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section - Simplified */}
        <div className="text-center mb-16">
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center bg-primary-500/10 border border-primary-200 text-primary-700 text-sm font-semibold py-2 px-4 rounded-full mb-4">
              <span className="w-2 h-2 bg-primary-500 rounded-full mr-2"></span>
              Egyptian Innovation, Global Impact
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Pioneering <span className="text-primary-600">Solar Excellence</span>
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              From our roots in Cairo to transforming solar energy landscapes across the MENA region
            </p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Content */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6">
              Revolutionizing Solar Energy
            </h3>
            
            <div className="space-y-4 text-gray-600">
              <p>
                <span className="font-semibold text-primary-600">DuaAxis</span> was founded in Cairo with a mission to maximize solar energy production through cutting-edge dual-axis tracking technology.
              </p>
              
              <p>
                Our systems are specifically engineered for Egypt's unique climate, featuring advanced dust mitigation and superior heat resistance.
              </p>
              
              <p>
                We combine precision engineering with Egyptian innovation to create solar tracking systems that are both efficient and perfectly adapted to regional conditions.
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 mt-6">
              {statistics.map((stat, index) => (
                <div 
                  key={index} 
                  className={`bg-white rounded-xl p-4 shadow-md border text-center transition-all duration-300 ${
                    index === currentStat ? 'ring-2 ring-primary-200 scale-105' : 'hover:scale-105'
                  }`}
                >
                  <div className="text-2xl mb-2">{stat.icon}</div>
                  <div className={`text-xl font-bold ${index === currentStat ? 'text-primary-600' : 'text-gray-700'} mb-1`}>
                    {stat.value}
                  </div>
                  <div className="text-sm font-semibold text-gray-900">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Visual Section */}
          <div className={`transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  className="w-full h-64 lg:h-80 object-cover"
                  src={solarImageUrl}
                  alt="DuaAxis solar tracking system"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div 
                  className="absolute inset-0 bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white text-lg font-semibold"
                  style={{ display: 'none' }}
                >
                  DuaAxis Solar Technology
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className={`mb-16 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Our Core Values</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-4 text-center shadow-md border hover:shadow-lg transition-all duration-300"
              >
                <div className={`w-12 h-12 bg-gradient-to-r ${value.color} rounded-xl flex items-center justify-center text-white text-xl mx-auto mb-3`}>
                  {value.icon}
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h4>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-16">
          <div className={`bg-primary-600 rounded-2xl p-6 text-white transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h3 className="text-xl font-bold mb-4">Our Mission</h3>
            <p className="text-primary-100">
              To accelerate Egypt's renewable energy transition by providing efficient and reliable solar tracking technology.
            </p>
          </div>

          <div className={`bg-secondary-600 rounded-2xl p-6 text-white transition-all duration-700 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h3 className="text-xl font-bold mb-4">Our Vision</h3>
            <p className="text-secondary-100">
              To be the leader in solar technology across the MENA region, driving sustainable development through innovation.
            </p>
          </div>
        </div>

        {/* Timeline */}
        <div className={`mb-16 transition-all duration-700 delay-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Our Journey</h3>
          <div className="relative">
            <div className="absolute left-4 lg:left-1/2 transform lg:-translate-x-1/2 w-1 bg-primary-300 h-full rounded-full"></div>
            
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div 
                  key={index} 
                  className="relative flex items-start lg:items-center cursor-pointer"
                  onClick={() => setActiveMilestone(index)}
                >
                  {/* Timeline Node */}
                  <div className={`relative w-4 h-4 bg-primary-600 rounded-full border-2 border-white shadow-lg z-10 mr-4 lg:mx-auto ${
                    activeMilestone === index ? 'scale-125 ring-2 ring-primary-300' : ''
                  }`}></div>
                  
                  {/* Content */}
                  <div className={`flex-1 bg-white rounded-xl p-4 shadow-md border transition-all duration-300 ${
                    activeMilestone === index ? 'ring-2 ring-primary-200' : ''
                  }`}>
                    <div className="flex items-center mb-2">
                      <span className="text-xl mr-2">{milestone.icon}</span>
                      <h4 className="text-lg font-bold text-gray-900">{milestone.event}</h4>
                    </div>
                    <p className="text-primary-600 font-semibold text-sm mb-2">{milestone.year}</p>
                    <p className="text-gray-600 text-sm mb-3">{milestone.description}</p>
                    
                    <div className="flex flex-wrap gap-1">
                      {milestone.achievements.map((achievement, idx) => (
                        <span 
                          key={idx}
                          className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded"
                        >
                          {achievement}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className={`transition-all duration-700 delay-800 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-4">Our Leadership</h3>
          <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
            World-class expertise driving Egypt's solar revolution
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {teamMembers.map((member, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-4 text-center shadow-md border hover:shadow-lg transition-all duration-300"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-xl font-bold">
                  {member.name.split(' ').map(n => n[0]).join('')}
                </div>

                <h4 className="text-lg font-bold text-gray-900 mb-1">{member.name}</h4>
                <p className="text-primary-600 font-semibold text-sm mb-2">{member.role}</p>
                <p className="text-gray-600 text-xs mb-3">{member.expertise}</p>
                
                <div className="flex flex-wrap justify-center gap-1">
                  {member.achievements.slice(0, 2).map((achievement, idx) => (
                    <span 
                      key={idx}
                      className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded"
                    >
                      {achievement}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={`text-center mt-12 transition-all duration-700 delay-900 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-white rounded-2xl p-8 shadow-lg border">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Power Your Future?
            </h3>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              Join the solar revolution with Egyptian innovation
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                onClick={scrollToContact}
                className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
              >
                Contact Our Team
              </button>
              <button 
                onClick={scrollToFeatures}
                className="border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
              >
                Explore Technology
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Get a personalized solar analysis within 24 hours
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;